import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Button from "components/button/button";
import GlimpsePopup from "components/popup/popup.component";
import { displayError, displaySuccess } from "components/Toast/toast.component";
import {
  deletePartnerData,
  deleteUserData,
  previewPartnerDeletion,
  previewUserDeletion,
} from "services/data-deletion.service";
import { IDataDeletionLog, IDeletionSummary } from "interfaces/data-deletion.interface";

interface DataDeletionModalProps {
  open: boolean;
  mode: "partner" | "user";
  targetId: string;
  /** Partner name or user email; the admin has to type it to confirm. */
  confirmValue: string;
  onClose: () => void;
  onDeleted?: (log: IDataDeletionLog) => void;
}

const SummaryRows: React.FC<{ summary: IDeletionSummary; mode: "partner" | "user" }> = ({
  summary,
  mode,
}) => {
  const rows: [string, number | string][] =
    mode === "partner"
      ? [
          ["Students", summary.students],
          ["School staff accounts", summary.staff],
          ["Pending invitations", summary.pending_invitations],
          ["Sherpa chat messages", summary.row_counts.chat_message || 0],
          ["Uploaded files", summary.files],
        ]
      : [
          ["Sherpa chat messages", summary.row_counts.chat_message || 0],
          ["Careers", summary.row_counts.user_career || 0],
          ["Badges", summary.row_counts.badge_user || 0],
          ["Uploaded files", summary.files],
        ];

  return (
    <div className="flex flex-col gap-2 p-4 rounded-lg bg-border/40 text-body">
      <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
        {rows.map(([label, value]) => (
          <span key={label} className="contents">
            <span>{label}</span>
            <span className="font-semibold text-right">{value}</span>
          </span>
        ))}
      </div>
      <details>
        <summary className="cursor-pointer text-text-muted">All rows by table</summary>
        <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 pt-2">
          {Object.entries(summary.row_counts).map(([table, count]) => (
            <span key={table} className="contents">
              <span className="text-text-muted">{table}</span>
              <span className="text-right">{count}</span>
            </span>
          ))}
        </div>
      </details>
    </div>
  );
};

const DataDeletionModal: React.FC<DataDeletionModalProps> = ({
  open,
  mode,
  targetId,
  confirmValue,
  onClose,
  onDeleted,
}) => {
  const [deletePartnerRecord, setDeletePartnerRecord] = useState(true);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setDeletePartnerRecord(true);
    }
  }, [open, targetId]);

  const { data: preview, isFetching: isPreviewLoading, isError } = useQuery({
    queryKey: ["DataDeletionPreview", mode, targetId, deletePartnerRecord],
    queryFn: (): Promise<IDeletionSummary> =>
      mode === "partner"
        ? previewPartnerDeletion(targetId, deletePartnerRecord)
        : previewUserDeletion(targetId),
    enabled: open && !!targetId,
    refetchOnWindowFocus: false,
    retry: 0,
    gcTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      mode === "partner"
        ? deletePartnerData(targetId, confirmText, deletePartnerRecord)
        : deleteUserData(targetId, confirmText),
    retry: 0,
    onSuccess: (log) => {
      displaySuccess(
        mode === "partner"
          ? `Deleted ${log.users_deleted} account(s) and all related data.`
          : "User and all related data permanently deleted."
      );
      if (log.files_failed) {
        displayError(`${log.files_failed} uploaded file(s) could not be removed. Check the server logs.`);
      }
      onClose();
      onDeleted?.(log);
    },
    onError: (error: any) => {
      // The shared http interceptor rejects with a pre-formatted string.
      displayError(
        typeof error === "string"
          ? error
          : error?.response?.data?.detail || error?.message || "Deletion failed."
      );
    },
  });

  const confirmMatches =
    mode === "partner"
      ? confirmText.trim() === confirmValue.trim()
      : confirmText.trim().toLowerCase() === confirmValue.trim().toLowerCase();

  return (
    <GlimpsePopup
      title=""
      popup={open}
      onClose={onClose}
      showCross={false}
      customClass="max-w-[460px]"
    >
      <div className="text-headerBlue text-[20px]">
        {mode === "partner" ? "Delete partner and all user data" : "Delete user and all data"}
      </div>
      <div className="text-body">
        {mode === "partner" ? (
          <>
            This permanently deletes every student and staff account at
            <span className="px-1 font-bold">{confirmValue}</span>
            and everything tied to them: profiles, Sherpa chats, activity, badges, invitations and
            uploaded files.
          </>
        ) : (
          <>
            This permanently deletes
            <span className="px-1 font-bold">{confirmValue}</span>
            and everything tied to them: profile, Sherpa chats, activity, badges and uploaded files.
          </>
        )}{" "}
        This cannot be undone.
      </div>

      {mode === "partner" && (
        <label className="flex items-center gap-2 text-body">
          <input
            type="checkbox"
            checked={deletePartnerRecord}
            onChange={(e) => setDeletePartnerRecord(e.target.checked)}
          />
          Also delete the partner account itself
        </label>
      )}

      {isPreviewLoading && <div className="text-body text-text-muted">Loading what will be deleted…</div>}
      {isError && !isPreviewLoading && (
        <div className="text-body text-action-destructive">Could not load a preview.</div>
      )}
      {preview && !isPreviewLoading && <SummaryRows summary={preview} mode={mode} />}

      <div className="flex flex-col gap-2">
        <div className="text-body">
          Type <span className="font-semibold">{confirmValue}</span> to confirm.
        </div>
        <input
          className="w-full h-[40px] rounded-lg border border-border px-3 text-body focus:outline-none"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
        />
      </div>

      <div className="flex justify-between mt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={!confirmMatches || !preview || deleteMutation.isPending}
          onClick={() => deleteMutation.mutate()}
        >
          {deleteMutation.isPending ? "Deleting…" : "Permanently delete"}
        </Button>
      </div>
    </GlimpsePopup>
  );
};

export default DataDeletionModal;
